import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, X, Sparkles, ShieldCheck, Star, Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SEO from "@/components/SEO";

// Outcome-based tier ladder (Feb 2026)
const TIER_FEATURES = {
  free: {
    tagline: "Get on the map — free forever, no card needed.",
    features: [
      { txt: "Company name, logo & 1 hero image",       ok: true  },
      { txt: "Website link + 1 category",               ok: true  },
      { txt: "Short description",                       ok: true  },
      { txt: "Matched RFQ invites",                     ok: false },
      { txt: "Individual product pages",                ok: false },
      { txt: "Boosted or premium placement",            ok: false },
    ],
  },
  starter: {
    tagline: "Get discovered — the essentials + matched RFQs.",
    features: [
      { txt: "1 category listing",                            ok: true  },
      { txt: "Public profile page live in 24 hours",          ok: true  },
      { txt: "3 profile images",                              ok: true  },
      { txt: "Up to 5 matched RFQ invites / month",           ok: true  },
      { txt: "Manage RFQs in your dashboard",                 ok: true  },
      { txt: "Individual product pages",                      ok: false },
      { txt: "Boosted or premium placement",                  ok: false },
    ],
  },
  growth: {
    tagline: "Serious suppliers — full catalogue + boosted rank.",
    features: [
      { txt: "Up to 10 category listings",             ok: true },
      { txt: "Individual product pages",               ok: true },
      { txt: "Up to 50 product images",                ok: true },
      { txt: "Up to 25 RFQs / month",                  ok: true },
      { txt: "Boosted ranking within category",        ok: true },
      { txt: "Priority email support",                 ok: true },
      { txt: "Guaranteed premium placement",           ok: false },
    ],
  },
  leader: {
    tagline: "Own your category — premium placement guaranteed.",
    features: [
      { txt: "Unlimited category listings",            ok: true },
      { txt: "Unlimited products & images",            ok: true },
      { txt: "Up to 100 RFQs / month",                 ok: true },
      { txt: "Guaranteed premium placement — front page every day", ok: true },
      { txt: "Minimum 120 monthly impressions guaranteed",           ok: true },
      { txt: "Daily rotation across 8 time slots (3h each) for fair top-4 exposure", ok: true },
      { txt: "Dedicated account manager",              ok: true },
      { txt: "Multi-brand support",                    ok: false },
    ],
  },
  enterprise: {
    tagline: "Multi-brand, multi-user, priority sponsorship.",
    features: [
      { txt: "Everything in Category Leader",          ok: true },
      { txt: "Multi-brand profiles (linked accounts)", ok: true },
      { txt: "Multi-user access & roles",              ok: true },
      { txt: "Up to 500 RFQs / month",                 ok: true },
      { txt: "Bulk upload API access",                 ok: true },
      { txt: "Priority sponsorship slots",             ok: true },
      { txt: "White-glove onboarding",                 ok: true },
      { txt: "Custom contracts & invoicing",           ok: true },
    ],
  },
};

const ORDERED = ["starter", "growth", "leader", "enterprise"];
// Iter48: rename Starter tier for clarity
const TIER_LABEL_OVERRIDES = { starter: "RFQ Starter" };

export default function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [slots, setSlots] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [waitEmail, setWaitEmail] = useState("");
  const [waitBusy, setWaitBusy] = useState(false);
  const [waitJoined, setWaitJoined] = useState(false);
  const [checkingOut, setCheckingOut] = useState(null); // tier id currently being processed

  useEffect(() => {
    api.get("/pricing").then(({ data }) => {
      const idx = Object.fromEntries(data.map((p) => [p.id, p]));
      // Iter48: apply label overrides.
      Object.entries(TIER_LABEL_OVERRIDES).forEach(([id, label]) => {
        if (idx[id]) idx[id] = { ...idx[id], label };
      });
      const sorted = ORDERED.map((id) => idx[id]).filter(Boolean);
      setPlans(sorted);
    });
    api.get("/founding-slots").then(({ data }) => setSlots(data)).catch(() => {});
  }, []);

  // Tick countdown every second based on server-supplied deadline
  useEffect(() => {
    if (!slots?.deadline_iso) return;
    const tick = () => {
      const diff = new Date(slots.deadline_iso).getTime() - Date.now();
      if (diff <= 0) {
        setCountdown({ d: 0, h: 0, m: 0, s: 0, expired: true });
        return;
      }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setCountdown({ d, h, m, s, expired: false });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [slots]);

  const joinWaitlist = async (e) => {
    e.preventDefault();
    if (!waitEmail.trim()) return;
    setWaitBusy(true);
    try {
      await api.post("/subscribe", { email: waitEmail.trim(), role: "advertiser", source: "waitlist" });
      setWaitJoined(true);
      toast.success("You're on the Founding Supplier waitlist. We'll email you when a slot opens.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setWaitBusy(false);
    }
  };

  // Iter80 · Promo code entered before checkout. Persisted in sessionStorage so it
  // survives the Register → auto-checkout flow for logged-out visitors.
  const [promoCode, setPromoCode] = useState(() => {
    try { return sessionStorage.getItem("as_promo_code") || ""; } catch { return ""; }
  });
  const [promoStatus, setPromoStatus] = useState(null); // { ok, description } | { error }
  const [validatingPromo, setValidatingPromo] = useState(false);
  const applyPromo = async () => {
    const code = promoCode.trim();
    if (!code) { setPromoStatus(null); try { sessionStorage.removeItem("as_promo_code"); } catch {} return; }
    setValidatingPromo(true);
    try {
      const { data } = await api.get(`/payments/validate-promo?code=${encodeURIComponent(code)}`);
      setPromoStatus({ ok: true, description: data.description });
      try { sessionStorage.setItem("as_promo_code", code.toUpperCase()); } catch {}
      toast.success(`${data.code} · ${data.description}`);
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not validate promo code";
      setPromoStatus({ ok: false, error: msg });
      try { sessionStorage.removeItem("as_promo_code"); } catch {}
      toast.error(msg);
    } finally { setValidatingPromo(false); }
  };

  // Single click handler for every "Get started" button.
  // - Logged out or non-advertiser: send to Register with ?upgrade=<tier>. Register redirects to /dashboard?upgrade=<tier> which auto-fires checkout.
  // - Logged-in advertiser: create a Stripe subscription checkout session immediately and redirect to Stripe.
  const startCheckout = async (tierId) => {
    if (!user || user.role !== "advertiser") {
      navigate(`/register?upgrade=${encodeURIComponent(tierId)}`);
      return;
    }
    setCheckingOut(tierId);
    try {
      const { data } = await api.post("/payments/subscribe", {
        tier: tierId,
        origin_url: window.location.origin,
        // Iter80 · only send the code if it validated successfully, otherwise let Stripe show its own field
        promo_code: promoStatus?.ok ? promoCode.trim().toUpperCase() : undefined,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed. Please try again.");
      setCheckingOut(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="pricing-page">
      <SEO
        title="Pricing"
        description="Founding Supplier plans — $29 Starter to $499 Enterprise. Locked-in launch pricing for 12 months, 50-day pinned placement, and matched RFQ invites."
        path="/pricing"
      />
      <div className="text-center mb-6 max-w-2xl mx-auto">
        <div className="tracking-overline mb-3 text-[#B35C4A]">For suppliers · Founding cohort</div>
        <h1 className="font-serif text-4xl md:text-5xl text-[#11211A] tracking-tight" data-testid="pricing-headline">
          Founding Supplier Plans
        </h1>
        {(!slots || slots.remaining > 0) && (
          <div className="mt-5 inline-flex items-center gap-2 border-l-2 border-[#B8860B] bg-[#FFFBEB] px-4 py-2 text-left" data-testid="founding-lock-in-message">
            <Star className="w-4 h-4 text-[#B8860B] fill-current shrink-0" />
            <span className="text-sm text-[#11211A] font-medium">
              Become a Founding Supplier and lock in your rate for 12 months. Price increases as the platform grows.
            </span>
          </div>
        )}
        {slots && slots.remaining === 0 && (
          <div className="mt-5 inline-flex items-center gap-2 border-l-2 border-[#4A5550] bg-[#F0EFEA] px-4 py-2" data-testid="founding-slots-counter">
            <span className="text-sm text-[#4A5550] italic">Founding cohort filled — join the waitlist below.</span>
          </div>
        )}

        {/* 30-day countdown timer */}
        {countdown && slots?.remaining > 0 && !countdown.expired && (
          <div className="mt-6 max-w-lg mx-auto" data-testid="founding-countdown">
            <div className="tracking-overline text-[#B35C4A] mb-2 flex items-center justify-center gap-2">
              <Clock className="w-3.5 h-3.5" />
              Founding pricing locks in
            </div>
            <div className="flex justify-center gap-3 text-[#11211A]">
              {[
                { v: countdown.d, l: "days" },
                { v: countdown.h, l: "hours" },
                { v: countdown.m, l: "min" },
                { v: countdown.s, l: "sec" },
              ].map((seg) => (
                <div key={seg.l} className="text-center">
                  <div className="font-serif text-3xl leading-none tabular-nums bg-white border border-[#E8ECE9] px-3 py-2 min-w-[56px]">
                    {String(seg.v).padStart(2, "0")}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#7D8C85] mt-1">{seg.l}</div>
                </div>
              ))}
            </div>
            <p className="text-xs text-[#7D8C85] mt-3 text-center italic">
              Founding rates are locked for 12 months from signup.
            </p>
          </div>
        )}

        <p className="text-[#4A5550] mt-5">
          Buyers browse for free, forever. Suppliers pay a flat monthly rate — no listing fees, no per-lead commissions.
          Lock in launch pricing for 12 months and get the Founding Supplier Badge.
        </p>
      </div>

      {/* Risk reversal guarantee */}
      <div className="max-w-3xl mx-auto mb-14 border-2 border-[#264839] bg-[#F0EFEA] p-6 flex flex-col md:flex-row items-start gap-4" data-testid="risk-reversal-banner">
        <div className="w-12 h-12 rounded-full bg-[#264839] text-white flex items-center justify-center shrink-0">
          <ShieldCheck className="w-5 h-5" />
        </div>
        <div>
          <div className="tracking-overline mb-1 text-[#264839]">Risk-reversal guarantee</div>
          <p className="text-[#11211A] font-medium leading-relaxed">
            If you don't receive 3+ matched RFQs in your first 60 days, we extend your plan free.
          </p>
          <p className="text-xs text-[#4A5550] mt-1">No fine print. If the leads don't show up, neither should the invoice.</p>
        </div>
      </div>

      {/* Iter80 · Promo code entry — one field applies to whichever plan the visitor picks */}
      <div className="max-w-3xl mx-auto mb-14 bg-white border border-[#E8ECE9] p-5" data-testid="promo-code-panel">
        <div className="flex flex-col md:flex-row md:items-end gap-3">
          <div className="flex-1">
            <label htmlFor="promo-code-input" className="tracking-overline text-[#B8860B] mb-1 block">
              Have a promo code?
            </label>
            <input
              id="promo-code-input"
              type="text"
              value={promoCode}
              onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoStatus(null); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyPromo(); } }}
              placeholder="e.g. FOUNDING50"
              maxLength={40}
              className="w-full px-3 py-2 border border-[#C4CFC9] bg-white text-[#11211A] tracking-wider uppercase font-medium focus:outline-none focus:border-[#264839]"
              data-testid="promo-code-input"
            />
          </div>
          <button
            type="button"
            onClick={applyPromo}
            disabled={validatingPromo || !promoCode.trim()}
            className="btn-primary disabled:opacity-50 whitespace-nowrap"
            data-testid="promo-code-apply"
          >
            {validatingPromo ? "Checking…" : "Apply code"}
          </button>
        </div>
        {promoStatus?.ok && (
          <div
            className="mt-3 text-sm text-[#264839] bg-[#E9F1EC] border-l-2 border-[#264839] px-3 py-2 flex items-center gap-2"
            data-testid="promo-code-success"
          >
            <Check className="w-4 h-4" />
            <span><span className="font-semibold">{promoCode.trim().toUpperCase()}</span> · {promoStatus.description} — applied automatically at checkout.</span>
          </div>
        )}
        {promoStatus && !promoStatus.ok && (
          <div
            className="mt-3 text-sm text-[#B35C4A] bg-[#FBECE8] border-l-2 border-[#B35C4A] px-3 py-2"
            data-testid="promo-code-error"
          >
            {promoStatus.error}
          </div>
        )}
        {!promoStatus && (
          <p className="mt-3 text-xs text-[#7D8C85] italic">
            Applies automatically at Stripe checkout when you pick a plan below.
          </p>
        )}
      </div>

      {/* Iter83 — Free vs Founding Supplier comparison */}
      <div className="max-w-5xl mx-auto mb-16" data-testid="tier-comparison">
        <div className="text-center mb-8">
          <div className="tracking-overline mb-3">Two ways to be listed</div>
          <h2 className="font-serif text-3xl md:text-4xl text-[#11211A] tracking-tight">Free listing or Founding Supplier?</h2>
          <p className="text-sm text-[#4A5550] mt-3 max-w-2xl mx-auto leading-relaxed">
            Every supplier can be in the directory for free. Founding Suppliers get the full profile, matched RFQs and priority placement above every free listing.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full table-fixed text-sm border border-[#E8ECE9] bg-white" data-testid="tier-comparison-table">
            <colgroup>
              <col className="w-[40%]" />
              <col className="w-[30%]" />
              <col className="w-[30%]" />
            </colgroup>
            <thead>
              <tr className="border-b border-[#E8ECE9]">
                <th className="text-left p-4 font-normal text-[#7D8C85]">What you get</th>
                <th className="p-4 text-center align-top border-l border-[#E8ECE9]" data-testid="comparison-col-free">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#7D8C85]">Free Listing</div>
                  <div className="font-serif text-2xl text-[#11211A] mt-1">$0</div>
                  <div className="text-[11px] text-[#7D8C85]">forever</div>
                </th>
                <th className="p-4 text-center align-top border-l border-[#E8ECE9]" data-testid="comparison-col-founding">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#7D8C85]">Founding Supplier</div>
                  <div className="font-serif text-2xl text-[#11211A] mt-1">from ${plans[0]?.amount ?? 29}</div>
                  <div className="text-[11px] text-[#7D8C85]">/ month · 4 plans below</div>
                </th>
              </tr>
            </thead>
            <tbody>
              {[
                ["Company name, description, category & country", true, true],
                ["Website link on your profile", true, true],
                ["Self-manage your listing after claiming", true, true],
                ["Logo, hero image & photo gallery", false, true],
                ["Individual product pages", false, true],
                ["Receive matched RFQs from hotel buyers", false, true],
                ["Buyers can send you quote requests directly", false, true],
                ["Priority placement — shown above all free listings", false, true],
                ["Founding Supplier badge", false, true],
                ["Featured / Category Leader placement", false, true],
                ["Locked-in launch pricing for 12 months", false, true],
              ].map(([label, free, paid]) => (
                <tr key={label} className="border-b border-[#E8ECE9] last:border-0">
                  <td className="p-3 pl-4 text-[#4A5550]">{label}</td>
                  <td className="p-3 text-center border-l border-[#E8ECE9]">{free ? <Check className="w-4 h-4 text-[#264839] inline" /> : <X className="w-4 h-4 text-[#C4CFC9] inline" />}</td>
                  <td className="p-3 text-center border-l border-[#E8ECE9]">{paid ? <Check className="w-4 h-4 text-[#264839] inline" /> : <X className="w-4 h-4 text-[#C4CFC9] inline" />}</td>
                </tr>
              ))}
              <tr>
                <td className="p-4" />
                <td className="p-4 text-center border-l border-[#E8ECE9]">
                  <button type="button" onClick={() => navigate(user ? "/dashboard" : "/register?role=advertiser&plan=free")} className="btn-secondary w-full justify-center" data-testid="plan-cta-free">
                    List your company free
                  </button>
                </td>
                <td className="p-4 text-center border-l border-[#E8ECE9]">
                  <a href="#founding-plans" className="btn-secondary w-full justify-center" data-testid="plan-cta-founding-scroll">
                    Choose a Founding plan ↓
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Iter55 — Founding Supplier benefits (visible to non-subscribers) */}
      <div className="max-w-5xl mx-auto mb-14" data-testid="founding-benefits">
        <div className="text-center mb-8">
          <div className="tracking-overline mb-3 text-[#B8860B]">Founding Supplier Perks · Auto-applied at signup</div>
          <h2 className="font-serif text-3xl md:text-4xl text-[#11211A] tracking-tight">Three benefits you can only get right now</h2>
          <p className="text-sm text-[#4A5550] mt-3 max-w-2xl mx-auto leading-relaxed">
            Every supplier who subscribes during the launch cohort is automatically tagged as a Founding Supplier — no promo code, no upsell call. The perks stack on top of your chosen tier.
          </p>
        </div>
        <div className="grid sm:grid-cols-3 gap-6">
          {[
            {
              icon: "🔒",
              title: "Locked-in pricing",
              body: "Your monthly rate is grandfathered for 12 months. Future price rises don't affect Founding Suppliers.",
            },
            {
              icon: "🏅",
              title: "Founding Supplier Badge",
              body: "A visible gold badge on your public profile and every listing — buyers instantly recognise early trusted suppliers.",
            },
            {
              icon: "📌",
              title: "50-day pinned placement",
              body: "Auto-pinned to the top of your category (starting with Linen, Textiles & Soft Furnishings) for 50 days after signup — extra reach while buyers arrive.",
            },
          ].map((b) => (
            <div key={b.title} className="bg-white border border-[#E8ECE9] border-l-4 border-l-[#B8860B] p-5" data-testid={`founding-benefit-${b.title.toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'')}`}>
              <div className="text-2xl mb-2" aria-hidden="true">{b.icon}</div>
              <div className="font-serif text-lg text-[#11211A] leading-tight mb-1.5">{b.title}</div>
              <p className="text-xs text-[#4A5550] leading-relaxed">{b.body}</p>
            </div>
          ))}
        </div>
      </div>

      <div id="founding-plans" className="text-center mb-8 scroll-mt-24" data-testid="founding-plans-heading">
        <div className="tracking-overline mb-3 text-[#B8860B]">Founding Supplier · Paid plans</div>
        <h2 className="font-serif text-3xl md:text-4xl text-[#11211A] tracking-tight">Pick the Founding Supplier plan that fits</h2>
        <p className="text-sm text-[#4A5550] mt-3 max-w-2xl mx-auto">All four plans include Founding Supplier status, RFQ access and placement above free listings.</p>
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
        {plans.map((p) => {
          const cfg = TIER_FEATURES[p.id] || { features: [] };
          const isPopular = p.id === "leader";
          const isEnterprise = p.id === "enterprise";
          const isFree = p.id === "free";
          return (
            <div
              key={p.id}
              className={`bg-white border ${
                isPopular ? "border-[#264839] border-2" : isEnterprise ? "border-[#B35C4A] border-2" : isFree ? "border-[#C4CFC9] border-dashed" : "border-[#E8ECE9]"
              } p-8 relative flex flex-col`}
              data-testid={`plan-${p.id}`}
            >
              {isEnterprise && (
                <div className="absolute -top-3 left-8 bg-[#B35C4A] text-white text-[10px] uppercase tracking-[0.2em] px-3 py-1 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Multi-brand
                </div>
              )}
              {isFree && (
                <div className="absolute -top-3 left-8 bg-[#4A5550] text-white text-[10px] uppercase tracking-[0.2em] px-3 py-1">
                  Free forever
                </div>
              )}

              <div className="font-serif text-2xl text-[#11211A]" data-testid={`plan-label-${p.id}`}>
                {p.label}
              </div>
              <p className="text-sm text-[#7D8C85] mt-2 min-h-[40px]">{cfg.tagline}</p>

              <div className="mt-6 flex items-baseline gap-1">
                <span className="text-5xl font-serif text-[#11211A]" data-testid={`plan-price-${p.id}`}>
                  ${p.amount}
                </span>
                <span className="text-sm text-[#7D8C85]">/ month</span>
              </div>

              <ul className="mt-8 space-y-3 text-sm text-[#4A5550] flex-1">
                {cfg.features.map((f) => (
                  <li key={f.txt} className="flex items-start gap-2">
                    {f.ok
                      ? <Check className="w-4 h-4 text-[#264839] mt-0.5 shrink-0" />
                      : <X className="w-4 h-4 text-[#C4CFC9] mt-0.5 shrink-0" />}
                    <span className={f.ok ? "" : "text-[#9AA6A0] line-through"}>
                      {f.txt}
                      {f.note && f.ok && <span className="text-[#7D8C85] italic ml-1">({f.note})</span>}
                    </span>
                  </li>
                ))}
              </ul>

              {isFree ? (
                <button
                  type="button"
                  onClick={() => navigate(user ? "/dashboard" : "/register?role=advertiser&plan=free")}
                  className="btn-secondary w-full justify-center mt-8"
                  data-testid={`plan-cta-${p.id}`}
                >
                  Get Listed (Free)
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => startCheckout(p.id)}
                  disabled={checkingOut === p.id}
                  className={`${isPopular || isEnterprise ? "btn-primary" : "btn-secondary"} w-full justify-center mt-8 disabled:opacity-60 disabled:cursor-not-allowed`}
                  data-testid={`plan-cta-${p.id}`}
                >
                  {checkingOut === p.id
                    ? "Redirecting to Stripe…"
                    : `Get started · $${p.amount}/mo`}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Waitlist — always visible; auto-headlines change when cohort fills */}
      <div className="mt-14 max-w-3xl mx-auto border-2 border-[#B8860B]/40 bg-gradient-to-br from-[#FFFBEB] to-[#F0EFEA] p-8" data-testid="founding-waitlist">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-12 h-12 shrink-0 bg-[#B8860B] text-white flex items-center justify-center">
            <Star className="w-5 h-5 fill-current" />
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="tracking-overline text-[#B8860B] mb-1">
              {slots?.remaining === 0 ? "Founding cohort filled" : "Founding Supplier Waitlist"}
            </div>
            <div className="font-serif text-2xl text-[#11211A] mb-2">
              {slots?.remaining === 0
                ? "Get the next slot before it goes public."
                : "Be first if a Founding slot opens."}
            </div>
            <p className="text-sm text-[#4A5550] leading-relaxed max-w-lg">
              We take the Founding cohort seriously — 50 slots, hand-vetted, one shot at launch pricing.
              Drop your email and we'll ping you the moment a slot opens or the next cohort opens up.
            </p>

            {waitJoined ? (
              <div className="mt-5 flex items-center gap-2 text-[#264839] font-medium" data-testid="waitlist-success">
                <Check className="w-5 h-5" /> You're on the list — watch your inbox.
              </div>
            ) : (
              <form onSubmit={joinWaitlist} className="mt-5 flex flex-wrap gap-2" data-testid="waitlist-form">
                <input
                  type="email"
                  required
                  value={waitEmail}
                  onChange={(e) => setWaitEmail(e.target.value)}
                  placeholder="you@supplier.com"
                  className="flex-1 min-w-[220px] border border-[#B8860B]/40 bg-white px-4 py-3 text-sm text-[#11211A] placeholder:text-[#7D8C85] focus:outline-none focus:border-[#B8860B]"
                  data-testid="waitlist-email"
                />
                <button
                  type="submit"
                  disabled={waitBusy}
                  className="btn-primary disabled:opacity-60"
                  data-testid="waitlist-submit"
                >
                  {waitBusy ? "Adding…" : "Join the waitlist"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* How placement works */}
      <div className="mt-14 max-w-4xl mx-auto" data-testid="how-placement-works-section">
        <div className="text-center mb-8">
          <div className="tracking-overline mb-3 text-[#B35C4A]">How Placement Works</div>
          <h2 className="font-serif text-3xl text-[#11211A]">Transparency, not auctions.</h2>
          <p className="text-sm text-[#4A5550] mt-3 max-w-xl mx-auto">
            We don't sell placement to the highest bidder. Ranking follows a fixed, published order — so buyers see the strongest suppliers first and suppliers know exactly how to earn visibility.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {[
            {
              t: "1 · Category Leader Daily Rotation",
              b: "Every Category Leader ($199) is guaranteed premium placement — the top row of results on both their category page and the homepage. Multiple leaders in one category share the top-4 via a daily rotation across 8 three-hour time slots, with a minimum 120 impressions per month guaranteed."
            },
            {
              t: "2 · Growth Boost",
              b: "Catalogue Growth ($79) subscribers rank above Starter and Free listings inside their category, positioned right below the Leader rotation. Ordering within Growth follows join date."
            },
            {
              t: "3 · RFQ Starter",
              b: "RFQ Starter ($29) listings appear in category results without a placement boost. They receive up to 5 matched RFQ invites per month and are eligible for the capped RFQ matcher (max 5–8 suppliers per RFQ)."
            },
            {
              t: "4 · Founding Suppliers",
              b: "The first 50 suppliers keep locked-in launch pricing for 12 months, priority RFQ delivery, and the golden Founding Supplier badge. This is a one-time cohort — closed once slots fill."
            },
            {
              t: "5 · What we DON'T do",
              b: "No pay-to-play name gating — buyers always see every supplier's company name and profile card. No hidden bidding on RFQs. No commissions on won deals. What you pay is what you see."
            },
          ].map((s) => (
            <div key={s.t} className="border border-[#E8ECE9] bg-white p-5" data-testid="placement-rule-item">
              <div className="font-medium text-[#11211A] mb-1.5">{s.t}</div>
              <p className="text-xs text-[#4A5550] leading-relaxed">{s.b}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-10 border border-[#E8ECE9] p-6 bg-[#F0EFEA] max-w-3xl mx-auto text-sm text-[#4A5550]" data-testid="pricing-credit-packs">
        <div className="tracking-overline mb-2">Need more leads?</div>
        <div className="font-serif text-xl text-[#11211A] mb-2">Quote Credit Packs</div>
        <p>
          Once you hit your plan's monthly RFQ ceiling, top up with a pack — extra quotes stay in your account until
          you use them, no expiry. Contact us for volume pricing.
        </p>
      </div>

      <p className="text-xs text-center text-[#7D8C85] mt-8">
        Secured payments by Stripe. Upgrade any time from your dashboard.
      </p>
    </div>
  );
}
